import React from "react";
import { Button } from "@/shared/ui/Button/Button";
import styles from "./UpdateWindow.module.css";
import type { UpdateStatus } from "@/shared/types/update";

interface UpdateWindowProps {
  status: UpdateStatus;
  onUpdate: () => void;
  onClose?: () => void;
  visible: boolean;
}

export const UpdateWindow: React.FC<UpdateWindowProps> = ({
  status,
  onUpdate,
  onClose,
  visible,
}) => {
  if (!visible) return null;

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i]
    );
  };

  const formatSpeed = (bytesPerSecond: number): string => {
    return formatBytes(bytesPerSecond) + "/s";
  };

  const showUpdateButton =
    status.status === "update-available" || status.status === "downloaded";
  const showProgress =
    status.status === "downloading" && status.progress !== undefined;

  return (
    <div className={styles.panel}>
      <div className={styles.panelContent}>
        <div className={styles.panelHeader}>
          <span className={styles.panelTitle}>Обновление</span>
          {onClose && status.status !== "downloading" && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Закрыть"
            >
              ×
            </button>
          )}
        </div>

        <div className={styles.panelBody}>
          {status.status === "update-available" && (
            <p className={styles.panelText}>
              Доступно обновление {status.version}
            </p>
          )}
          {status.status === "downloading" && (
            <p className={styles.panelText}>Загрузка… {status.progress}%</p>
          )}
          {status.status === "downloaded" && (
            <p className={styles.panelText}>Готово к установке</p>
          )}

          {showProgress && (
            <div className={styles.progressContainer}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${status.progress}%` }}
                />
              </div>
              {status.speed && status.speed > 0 && (
                <span className={styles.progressSpeed}>
                  {formatSpeed(status.speed)}
                </span>
              )}
              {status.downloaded && status.total && (
                <span className={styles.progressSize}>
                  {formatBytes(status.downloaded)} / {formatBytes(status.total)}
                </span>
              )}
            </div>
          )}

          {showUpdateButton && (
            <Button
              variant="primary"
              onClick={onUpdate}
              size="sm"
              isLoading={status.status === "downloading"}
            >
              {status.status === "downloaded"
                ? "Установить и перезапустить"
                : "Обновить"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
